/**
 * /verify-email?token=… — step TWO of signup: the inbox is proven, now make the passkey.
 *
 * 🔴 This page exists because of an account-takeover hole. Signup used to be: type an
 * email → create a passkey. The server took the email from the CALLER, so anyone who
 * knew a client's address could bind their own Face ID to that client's account.
 *
 * Now the address is never typed here. It comes off the signup token — which exists only
 * because someone clicked a link in that inbox — and the server hands it back to us. The
 * ordering IS the fix: no proven inbox, no passkey, no account.
 *
 *   1. /register/begin { signup_token }   → the server resolves the email + a challenge
 *   2. navigator.credentials.create(...)  → Face ID / fingerprint
 *   3. /register/finish { token, … }      → the account is created, the token is burnt
 *   4. /login/begin + /login/finish       → straight in, no second prompt
 */

import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";
import { authenticatePasskey, registerPasskey } from "@re-platform/auth";
import { KeyRound, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import {
  loadOnboardingAgent,
  saveOnboardingAgent,
} from "../home/onboardingAgent";
import { passkeyApi } from "./passkeyApi";
import { saveSession } from "./session";
import "./SignIn.css";
import { PublicFooter } from "../../shell/PublicFooter";

type State =
  | { kind: "ready" }
  | { kind: "working"; stage: "prompt" | "creating" | "signing-in" }
  | { kind: "error"; message: string };

export function CompleteSignup() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [state, setState] = useState<State>({ kind: "ready" });
  const navigate = useNavigate();
  // The passkey prompt must be triggered by a user gesture on iOS/Safari, so this is a
  // BUTTON, not an effect. The ref only guards a double-click.
  const running = useRef(false);

  useEffect(() => {
    if (!token) {
      setState({
        kind: "error",
        message:
          "That link is missing its code. Please use the link from your email.",
      });
    }
  }, [token]);

  async function complete(): Promise<void> {
    if (running.current) return;
    running.current = true;
    setState({ kind: "working", stage: "prompt" });
    try {
      // The email comes BACK from the server; we never send one.
      const begin = await passkeyApi.registerBegin({ signup_token: token });

      const created = await registerPasskey(
        begin.public_key as unknown as PublicKeyCredentialCreationOptionsJSON,
      );

      setState({ kind: "working", stage: "creating" });
      const registered = await passkeyApi.registerFinish({
        challenge_id: begin.challenge_id,
        attestation: created.response as unknown as Record<string, unknown>,
        signup_token: token,
      });

      // Spec 023, precedence II: the agent whose page the visitor signed up on wins
      // — keep it when it is in the brokerage the account was created in. Otherwise
      // (another device: nothing stored) seed from the agent the server carried
      // through the signup. The invite token is dropped either way: the server has
      // recorded it.
      const stored = loadOnboardingAgent();
      if (stored?.agentId && stored.agentTenantId === registered.tenant_id) {
        const { inviteToken: _recorded, ...kept } = stored;
        saveOnboardingAgent(kept);
      } else if (registered.agent_id) {
        saveOnboardingAgent({
          agentId: registered.agent_id,
          agentTenantId: registered.tenant_id,
        });
      }

      // Straight in with the credential we just made. Registration commits AFTER its
      // response under Lambda, so this can race it and 404 — retry briefly (observed
      // live at Deploy Checkpoint #9: a 201 and a 404 in the same second).
      setState({ kind: "working", stage: "signing-in" });
      let login: Awaited<ReturnType<typeof passkeyApi.loginBegin>> | null =
        null;
      for (let attempt = 1; attempt <= 4; attempt++) {
        try {
          login = await passkeyApi.loginBegin({ email: begin.email });
          break;
        } catch (err) {
          if (attempt === 4) throw err;
          await new Promise((r) => setTimeout(r, 800 * attempt));
        }
      }
      const assertion = await authenticatePasskey(
        login!.public_key as unknown as PublicKeyCredentialRequestOptionsJSON,
      );
      const finish = await passkeyApi.loginFinish({
        challenge_id: login!.challenge_id,
        assertion: assertion.response as unknown as Record<string, unknown>,
      });

      saveSession({
        accessToken: finish.access_token,
        refreshToken: finish.refresh_token,
        expiresIn: finish.expires_in,
        userId: finish.user_id,
        tenantId: finish.tenant_id,
        primaryRole: finish.primary_role,
        email: finish.email,
        displayName: finish.display_name,
      });
      navigate("/");
    } catch (err) {
      running.current = false;
      setState({
        kind: "error",
        message:
          err instanceof Error
            ? err.message
            : "We couldn't finish setting up your account.",
      });
    }
  }

  return (
    <div className="si-page">
      <section className="si-card">
        <div className="si-icon">
          <KeyRound size={28} />
        </div>
        <h1 className="si-title">Email confirmed</h1>

        {state.kind === "error" ? (
          <>
            <p className="si-sub">{state.message}</p>
            <button
              type="button"
              className="si-btn"
              onClick={() => navigate("/sign-up")}
            >
              Start again
            </button>
          </>
        ) : state.kind === "working" ? (
          <p className="si-sub">
            <Loader2 className="si-spin" size={16} />{" "}
            {state.stage === "prompt"
              ? "Follow your device's prompt…"
              : state.stage === "creating"
                ? "Creating your account…"
                : "Signing you in…"}
          </p>
        ) : (
          <>
            <p className="si-sub">
              Now set up your sign-in. Use Face ID, your fingerprint or your
              screen lock — there's no password to remember, and it only works
              on your own device.
            </p>
            <button type="button" className="si-btn" onClick={complete}>
              Set up sign-in
            </button>
          </>
        )}
      </section>
      <PublicFooter />
    </div>
  );
}
